<script lang="ts">
  // Sparkline — stateless SVG bar chart.
  //
  // Visual reference: openspec/changes/ui-redesign-v2/Untitled.fig
  // nodes 0:125-148 (HostOverview MetricsGrid sparkline, 20 bars
  // showing host CPU history).
  //
  // Contract:
  //   - One <rect> per value; height normalised against the series max.
  //   - Bar widths auto-fit the viewBox so a 20-element series and a
  //     5-element series produce equally-spaced bars without the
  //     consumer having to compute geometry.
  //   - Stateless: pure function of `values`. The store-side history
  //     buffer lives in PageOverview, not here.
  //
  // The viewBox is `0 0 100 H` (default H=24). 1 px gutter top + bottom
  // keeps bars from clipping the rounded corners of the parent card.

  export let values: readonly number[] = [];
  export let height: number = 24;
  /** Optional extra class name(s) appended to `.sparkline`. */
  export let className: string = "";

  $: safeValues = Array.isArray(values) ? values : [];
  $: max = safeValues.length > 0 ? Math.max(...safeValues, 1) : 1;
  $: innerHeight = Math.max(2, height - 2); // 1 px gutter top + bottom
  $: barCount = safeValues.length;
  $: barWidth = barCount > 0 ? 100 / barCount : 100;
  $: barInset = barWidth * 0.18; // gap between adjacent bars
  $: bars = safeValues.map((v, i) => {
    const safe = Number.isFinite(v) ? Math.max(0, v) : 0;
    const h = (safe / max) * innerHeight;
    return {
      x: i * barWidth + barInset / 2,
      y: innerHeight - h + 1,
      width: Math.max(0.5, barWidth - barInset),
      height: Math.max(0.5, h),
    };
  });
</script>

<svg
  class="sparkline {className}"
  viewBox="0 0 100 {height}"
  preserveAspectRatio="none"
  aria-hidden="true"
  data-testid="sparkline"
>
  {#if bars.length === 0}
    <rect x="0" y="1" width="100" height={innerHeight} class="sparkline__skeleton" />
  {:else}
    {#each bars as bar, i (i)}
      <rect x={bar.x} y={bar.y} width={bar.width} height={bar.height} class="sparkline__bar" />
    {/each}
  {/if}
</svg>
